(() => {
  const statusSelector = '[data-infinite-scroll-status]';
  const sentinelSelector = '[data-infinite-scroll-sentinel]';
  const loadingSelector = '[data-infinite-scroll-loading]';
  const PREFETCH_BUFFER_PX = 2300;

  class CollectionInfiniteScroll {
    constructor() {
      this.observer = null;
      this.isLoading = false;
      this.scrollHandler = this.onScroll.bind(this);
      this.sectionId = '';
      this.grid = null;
      this.gridList = null;
      this.status = null;
      this.sentinel = null;
      this.loading = null;
    }

    refresh() {
      this.disconnectObserver();

      this.grid = document.getElementById('product-grid');
      if (!this.grid) return;
      this.gridList = this.resolveList(this.grid);
      if (!this.gridList) return;

      this.sectionId = this.grid.dataset.id || '';
      this.status = this.grid.parentElement?.querySelector(statusSelector) || document.querySelector(statusSelector);
      if (!this.status || !this.sectionId) return;

      this.sentinel = this.status.querySelector(sentinelSelector);
      this.loading = this.status.querySelector(loadingSelector);
      if (!this.sentinel) return;

      if (!this.getNextUrl()) return;
      this.observeSentinel();
      this.bindScrollFallback();
      this.onScroll();
    }

    getNextUrl() {
      return this.status?.dataset.nextUrl || '';
    }

    setNextUrl(url) {
      if (!this.status) return;
      this.status.dataset.nextUrl = url || '';
    }

    showLoading(show) {
      if (!this.loading) return;
      this.loading.classList.toggle('hidden', !show);
    }

    observeSentinel() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || this.isLoading) return;
            if (!this.getNextUrl()) return;
            this.loadNextPage();
          });
        },
        { rootMargin: `${PREFETCH_BUFFER_PX}px 0px`, threshold: 0 }
      );

      this.observer.observe(this.sentinel);
    }

    resolveList(root) {
      if (!root) return null;
      if (root.matches('ul, ol')) return root;
      return root.querySelector('ul, ol') || root;
    }

    bindScrollFallback() {
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    unbindScrollFallback() {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    onScroll() {
      if (this.isLoading || !this.getNextUrl()) return;
      const scrollBottom = window.scrollY + window.innerHeight;
      const triggerPoint = document.documentElement.scrollHeight - PREFETCH_BUFFER_PX;
      if (scrollBottom >= triggerPoint) {
        this.loadNextPage();
      }
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      this.unbindScrollFallback();
      this.isLoading = false;
      this.showLoading(false);
    }

    async loadNextPage() {
      const nextUrl = this.getNextUrl();
      if (!nextUrl || !this.sectionId) return;

      this.isLoading = true;
      this.showLoading(true);

      try {
        const url = new URL(nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.sectionId);

        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Infinite scroll request failed with ${response.status}`);

        const responseText = await response.text();
        const parsed = new DOMParser().parseFromString(responseText, 'text/html');
        const nextGrid = parsed.getElementById('product-grid');
        const nextGridList = this.resolveList(nextGrid);
        const nextStatus = parsed.querySelector(statusSelector);

        if (!nextGrid || !nextGridList || !nextStatus || !this.gridList) {
          this.setNextUrl('');
          this.disconnectObserver();
          return;
        }

        const items = Array.from(nextGridList.children);
        items.forEach((item) => {
          this.gridList.appendChild(item);
        });

        this.setNextUrl(nextStatus.dataset.nextUrl || '');
        if (!this.getNextUrl()) {
          this.disconnectObserver();
          return;
        }
      } catch (error) {
        console.error(error);
        this.disconnectObserver();
      } finally {
        this.isLoading = false;
        this.showLoading(false);
      }
    }
  }

  window.CollectionInfiniteScroll = window.CollectionInfiniteScroll || new CollectionInfiniteScroll();

  const bootInfiniteScroll = () => {
    window.CollectionInfiniteScroll.refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootInfiniteScroll);
  } else {
    bootInfiniteScroll();
  }

  document.addEventListener('shopify:section:load', bootInfiniteScroll);
  document.addEventListener('shopify:section:select', bootInfiniteScroll);
  window.addEventListener('pageshow', bootInfiniteScroll);
})();
